import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { nullAttrTypeOption } from '../GrowthAnalysis';
import ProfitGrowthDrivers from './ProfitGrowthDrivers';
import SubFilterDropdown from 'components/molecules/GrowthAnalysis/SubFilterDropdown';
import SubFilterCompareIcon from 'components/molecules/GrowthAnalysis/SubFilterCompareIcon';
import { FilterContextData } from 'contexts/FilterContext';
import { getConfig, parseAttributeType } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { cloneDeep, findLastIndex, uniqBy } from 'lodash';
import { useGrowthMarginDriversContext } from './context/GrowthMarginDriversContext';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { checkIfNullAttributePresent } from './data/hierarchicalTreeData';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import useProfitGrowthDriversData from './hooks/useProfitGrowthDriversData';
import { formatRawNumberForExport } from 'utils/helperMethods';
import useDecompositionGraphData from './hooks/useDecompositionGraphData';
import DecompositionSection from './DecompositionSection';

const DEFAULT_GROWTH_MARGIN_DRIVERS_PAGE_TITLE = 'Gross Margin Drivers';

const GrowthMarginDrivers = () => {
    const { appConfig } = useContext(UserContext);

    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultAttributeForData, excludedDimensions = [] } = profitPoolConfig;
    const pageTitle = profitPoolConfig?.growthMarginDriversPageTitle ?? DEFAULT_GROWTH_MARGIN_DRIVERS_PAGE_TITLE;
    const exportDecimals = getConfig('commonConfig', appConfig)?.exportDecimalPlaces ?? 7;
    const { allDimAndAttributeTypeList, timePeriodLoading } = useContext(FilterContextData);

    const [subFilterSelectedItems, setSubFilterSelectedItems] = useState([{ ...nullAttrTypeOption }]);
    const [subFilterDropdownOptions, setSubFilterDropdownOptions] = useState({ data: [], loading: false });
    const [activeCompareIcons, setActiveCompareIcons] = useState<any[]>([]);
    const [attributeTypeToShow, setAttributeTypesToShow] = useState<any[]>([]);

    const { setSelectedSubFilter, selectedGrowthMarginDriver } = useGrowthMarginDriversContext();

    const { decompositionGraphData } = useDecompositionGraphData();

    const lastItemInHierarchy = useRef<any>({
        dim1: null,
        dim2: null,
        dim3: null,
    });

    const profitGrowthDriversRef = useRef<HTMLDivElement>(null);
    const decompositionGraphRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                setSubFilterDropdownOptions({ data, loading: false });

                setSubFilterSelectedItems([
                    data.find((item: any) => item.label === defaultAttributeForData.attributeType) || data[0],
                    nullAttrTypeOption,
                ]);
            }
        }
    }, [allDimAndAttributeTypeList?.loading]);

    const formatAttrTypeItems = (item: any) => {
        return {
            selectedAttributeType: item.label,
            selectedAttributeValue: item.value,
            isAttribute: item.isAttribute,
            dimension: item.dimension,
            dataset: [],
            disableHover:
                lastItemInHierarchy?.current[item.dimension] === null
                    ? true
                    : lastItemInHierarchy?.current?.[item.dimension]?.value <= item.value, //disable hover in bar chart for last item
            key: Math.random() + item.label, //adding a random key here so that the item gets re-rendered again
            dataKey: item?.key,
        };
    };
    const arrangeAttrType = (index: number) => {
        return [
            formatAttrTypeItems(subFilterSelectedItems[index]),
            formatAttrTypeItems(subFilterSelectedItems[index + 1]),
        ];
    };

    useEffect(() => {
        // resetInfiniteScroller();
        //if comparison available, compare scenarios and show all scenarios user has compared
        if (activeCompareIcons.length > 0) {
            const sortedActiveComparisons = [...activeCompareIcons];
            sortedActiveComparisons.sort((a, b) => a - b); //sort active comparison indices in ascending order
            let tempAttrTypeToShow: any[] = [];

            for (let i = 0; i < sortedActiveComparisons.length; i++) {
                tempAttrTypeToShow.push(...arrangeAttrType(sortedActiveComparisons[i]));
            }
            tempAttrTypeToShow = uniqBy(tempAttrTypeToShow, (e) => e.selectedAttributeValue); //remove duplicate sub filter items
            setAttributeTypesToShow(tempAttrTypeToShow);
            setSelectedSubFilter(tempAttrTypeToShow[0]);

            //else if atleast 1 subFilter selected, only show latest sub filter selection
        } else if (subFilterSelectedItems?.length > 1) {
            for (let i = subFilterSelectedItems.length - 1; i >= 0; i--) {
                //finding the latest sub filter selection
                if (subFilterSelectedItems[i]?.value !== null) {
                    const tempAttrTypeToShow: any[] = [formatAttrTypeItems(subFilterSelectedItems[i])];
                    setSelectedSubFilter(tempAttrTypeToShow[0]);
                    setAttributeTypesToShow(tempAttrTypeToShow);
                    return;
                }
            }
            //else dont show anything
        } else {
            setAttributeTypesToShow([]);
            setSelectedSubFilter(defaultAttributeForData);
        }
    }, [activeCompareIcons, subFilterSelectedItems]);

    const memoisedSubFilterDropdownOptions = useMemo(() => {
        const tempOptions: any[] = cloneDeep(subFilterDropdownOptions.data);
        subFilterSelectedItems.forEach((item) => {
            const attrTypeIndex = tempOptions.findIndex((elem: any) => elem.label === item?.label);
            if (attrTypeIndex > -1) tempOptions.splice(attrTypeIndex, 1);
        });
        if (tempOptions?.length > 0) {
            const dim3LastItemIndex = findLastIndex(tempOptions, (item) => item.dimension === 'dim3');
            lastItemInHierarchy.current.dim1 =
                tempOptions[findLastIndex(tempOptions, (item) => item.dimension === 'dim1')] || null;
            lastItemInHierarchy.current.dim2 =
                tempOptions[findLastIndex(tempOptions, (item) => item.dimension === 'dim2')] || null;
            lastItemInHierarchy.current.dim3 = dim3LastItemIndex === -1 ? null : tempOptions[dim3LastItemIndex];
        } //always store the last item in hierarchy for different dimensions
        return tempOptions;
    }, [subFilterSelectedItems, subFilterDropdownOptions]);

    const removeComparisonActiveItem = (index: number, arr: any[]) => {
        const idx = arr.indexOf(index);
        if (idx > -1) arr.splice(idx, 1);
    };

    const onSubFilterOptionsChange = (val: any, item: any, index: number, filter?: any) => {
        const temp = [...subFilterSelectedItems];
        //if no item and val, it means event is triggered from clear icon
        if (!item && !val) {
            //if clearing last item and drilldown present

            temp.splice(index, 1); //remove item if cleared
            const tempActiveComparison = [...activeCompareIcons];
            removeComparisonActiveItem(index, tempActiveComparison); //remove comparison icon also on clearing sub filter
            if (index > 0) removeComparisonActiveItem(index - 1, tempActiveComparison); //remove both left and right side comparisons
            if (tempActiveComparison.includes(index + 1))
                tempActiveComparison.splice(tempActiveComparison.indexOf(index + 1), 1, index); //update the compare value
            setActiveCompareIcons(tempActiveComparison);
            const isNullPresent = checkIfNullAttributePresent(temp);
            if (!isNullPresent && temp.length < subFilterDropdownOptions.data?.length) temp.push(nullAttrTypeOption); //add a new null sub filter if no null sub filter exists and its within the limit
        } else {
            //resetting apply filter object if there is no drilldown present or
            //if drilldown present and changing sub filters except the last subfilter dropdown, reset the apply filter object

            temp.splice(index, 1, item); //replace item if changed
            if (temp.length < subFilterDropdownOptions.data?.length && temp.length - 1 === index)
                temp.push(nullAttrTypeOption); //add a new null sub filter if its within the limit
        }

        setSubFilterSelectedItems(temp);
    };

    const compareSubFilterClickHandler = (index: number, active: boolean) => {
        localStorage.setItem('compare_clicked', 'true');
        const tempActiveComparison = [...activeCompareIcons];
        if (!active) {
            // tempActiveComparison.push(index); //keep multiple active comparisons at a time
            tempActiveComparison[0] = index; //only keep 1 active comparison icon at a time
        } else {
            removeComparisonActiveItem(index, tempActiveComparison);
        }
        setActiveCompareIcons(tempActiveComparison);
    };

    // Get profit growth drivers data for CSV export
    const {
        waterFallChartData,
        currentYearData,
        previousYearData,
        isSuccess: isProfitGrowthDriversSuccess,
    } = useProfitGrowthDriversData();

    // Format profit growth drivers data for CSV/XLSX export
    const profitGrowthDriversExportData = useMemo(() => {
        if (!isProfitGrowthDriversSuccess || !waterFallChartData || waterFallChartData.length === 0) {
            return [];
        }

        const rows: any[] = [];

        // Add Year Ago row (as array for XLSX compatibility)
        rows.push(['Year Ago', formatRawNumberForExport(previousYearData || 0, exportDecimals), '']);

        // Add waterfall chart data rows
        waterFallChartData.forEach((item: any) => {
            const value = Array.isArray(item[selectedGrowthMarginDriver?.key || 'profit_absolute'])
                ? item[selectedGrowthMarginDriver?.key || 'profit_absolute'][1] -
                  item[selectedGrowthMarginDriver?.key || 'profit_absolute'][0]
                : item[selectedGrowthMarginDriver?.key || 'profit_absolute'] || 0;

            const growthLabel =
                item.trend === 'positive' ? 'Positive Growth' : item.trend === 'negative' ? 'Negative Growth' : '';

            rows.push([item.attributeDesc || '', formatRawNumberForExport(value, exportDecimals), growthLabel]);
        });

        // Add Current Year row
        rows.push(['Current Year', formatRawNumberForExport(currentYearData || 0, exportDecimals), '']);

        return rows;
    }, [
        waterFallChartData,
        currentYearData,
        previousYearData,
        isProfitGrowthDriversSuccess,
        selectedGrowthMarginDriver,
        exportDecimals,
    ]);

    return (
        <>
            <div className="flex gap-2 mb-4">
                {subFilterSelectedItems.map((item, index) => {
                    return (
                        <div key={index} className="flex gap-2 items-center">
                            <SubFilterDropdown
                                loading={allDimAndAttributeTypeList?.loading || timePeriodLoading || false}
                                onChange={onSubFilterOptionsChange}
                                key={index}
                                index={index}
                                options={memoisedSubFilterDropdownOptions}
                                value={item.label}
                                from={'growth-analysis'}
                            />
                            {subFilterSelectedItems?.length - 1 !== index && (
                                <SubFilterCompareIcon
                                    disabled={subFilterSelectedItems[index + 1]?.label === null}
                                    onClick={compareSubFilterClickHandler}
                                    index={index}
                                    active={activeCompareIcons.includes(index)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            <div
                className={`border border-borderLinePurpule bg-white p-4  rounded-lg            
            `}
            >
                <p className="font-semibold text-2xl">{pageTitle}</p>
                <div
                    className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-4 w-full"
                    ref={profitGrowthDriversRef}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex-1" />
                        <div className="flex justify-end gap-2">
                            <CopyToClipBoard refOfComponent={profitGrowthDriversRef} removeScrollbar />
                            {isProfitGrowthDriversSuccess && profitGrowthDriversExportData.length > 0 && (
                                <DownloadExcel
                                    title="Profit Growth Drivers"
                                    excelData={profitGrowthDriversExportData}
                                    headers={['Attribute', 'Value', 'Growth']}
                                    excludeFilters={true}
                                />
                            )}
                        </div>
                    </div>
                    <ProfitGrowthDrivers />
                </div>
                <div
                    className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-4 w-full"
                    id="decomposition-container-legacy"
                    ref={decompositionGraphRef}
                >
                    <DecompositionSection decompositionGraphRef={decompositionGraphRef} />
                </div>
            </div>
        </>
    );
};

export default GrowthMarginDrivers;
